import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { redemptionEndpoint, marketplaceReportsEndpoint, syngentaRedemptionEndpoint } from 'Services/Apis/clients';

export const LoadWallet = createAsyncThunk(
  'reportRedemption/LoadWallet',
  async (_, { getState }) => {
    const state = getState();
    const { oidc } = state;
    const participantId = oidc.user.profile['custom:participant_id'];
    const username = oidc.user.profile.username || oidc.user.profile['custom:login'];
    const walletData = await syngentaRedemptionEndpoint(oidc.user).get(`Wallet?username=${username}`, {
      headers: {
        'Content-Type': 'application/json',
        campaignId: oidc.user.profile['custom:campaign_id'],
        catalogId: oidc.user.profile['custom:catalog_id'],
        username,
        participantId,
        Authorization: `Bearer ${oidc.user.access_token}`,
      },
    });
    return walletData.data;
  },
);

export const LoadAllRedeems = createAsyncThunk(
  'reportRedemption/LoadAllRedeems',
  async (payload, { getState }) => {
    const state = getState();
    const { oidc } = state;
    const participantId = oidc.user.profile['custom:participant_id'];
    const username = oidc.user.profile.username || oidc.user.profile['custom:login'];

    const tableData = await marketplaceReportsEndpoint(oidc.user).post('/Order', payload, {
      headers: {
        'Content-Type': 'application/json',
        campaignId: oidc.user.profile['custom:campaign_id'],
        catalogId: oidc.user.profile['custom:catalog_id'],
        username,
        participantId,
        Authorization: `Bearer ${oidc.user.access_token}`,
      },
    });
    return tableData.data;
  },
);

export const GetRedemptionInfo = createAsyncThunk(
  'reportRedemption/GetRedeemInfo',
  async (param, { getState }) => {
    const state = getState();

    const { oidc } = state;
    const participantId = oidc.user.profile['custom:participant_id'];
    const username = oidc.user.profile.username || oidc.user.profile['custom:login'];

    const tableData = await marketplaceReportsEndpoint(oidc.user).post(`/Order/${param.redemptionId}`, { ...param.walletsLogin, orderDetailsFilterModel: 2 }, {
      headers: {
        'Content-Type': 'application/json',
        campaignId: oidc.user.profile['custom:campaign_id'],
        catalogId: oidc.user.profile['custom:catalog_id'],
        username,
        participantId,
        Authorization: `Bearer ${oidc.user.access_token}`,
      },
    });
    return tableData.data;
  },
);

export const LoadClcientsSolicitations = createAsyncThunk(
  'reportRedemption/LoadCLientsSolicitations',
  async (params, { getState }) => {
    const state = getState();
    const { oidc } = state;
    const participantId = oidc.user.profile['custom:participant_id'];
    const username = oidc.user.profile.username || oidc.user.profile['custom:login'];

    const queryParams = Object.entries(params)
      .filter(([key, value]) => value !== undefined && value !== '')
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    const tableData = await redemptionEndpoint(oidc.user).get(`Report?${queryParams}`, {
      headers: {
        'Content-Type': 'application/json',
        campaignId: oidc.user.profile['custom:campaign_id'],
        catalogId: oidc.user.profile['custom:catalog_id'],
        username,
        participantId,
        Authorization: `Bearer ${oidc.user.access_token}`,
      },
    });
    return tableData.data;
  },
);

const cpfLength = 11;

const formatCpfCnpj = (cpfCnpj) => {
  if (cpfCnpj.length === cpfLength) {
    return cpfCnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return cpfCnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const concatParticipantDocumentAndName = (participant) => {
  const { cpfCnpj, name } = participant;
  return `${formatCpfCnpj(cpfCnpj)} - ${name}`;
};

const initialState = {
  isLoadingWallet: false,
  isLoadingOrderDetails: false,
  isLoadingTable: false,
  walletInfo: [
    {
      cpfCpj: '',
      id: '',
      name: '',
      label: '',
      value: '',
    },
  ],
  allParticipantsLoginsOfWallet: [],
  tableInfo: [],
  pageSize: 15,
  currentPage: 1,
  orderDetailsInfo: {},
};

const ReportRedemptionSlice = createSlice({
  name: 'reduxToolkitReducerExample',
  initialState,
  reducers: {
    cleanTableInfo: (state) => {
      state.tableInfo = [];
    },
    resetPageNumber: (state) => {
      state.currentPage = 1;
    },
    changePageSize: (state, { payload }) => {
      state.pageSize = payload;
    },
    changeCurrentPage: (state, { payload }) => {
      state.currentPage = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(LoadWallet.pending, (state) => {
        state.isLoadingWallet = true;
      })
      .addCase(LoadWallet.fulfilled, (state, { payload }) => {
        const formattedNameAndDoc = payload.map((walletItem) => ({
          label: concatParticipantDocumentAndName(walletItem),
          value: walletItem.id,
        }));

        const formattedIds = payload.map((walletItem) => walletItem.id);

        state.allParticipantsLoginsOfWallet = formattedIds;

        state.walletInfo = formattedNameAndDoc;
        state.isLoadingWallet = false;
      })
      .addCase(LoadWallet.rejected, (state) => {
        state.isLoadingWallet = false;
      })
      .addCase(LoadAllRedeems.pending, (state) => {
        state.isLoadingTable = true;
      })
      .addCase(LoadAllRedeems.fulfilled, (state, { payload }) => {
        state.tableInfo = payload;
        state.isLoadingTable = false;
      })
      .addCase(LoadAllRedeems.rejected, (state) => {
        state.isLoadingTable = false;
      })
      .addCase(LoadClcientsSolicitations.pending, (state) => {
        state.isLoadingTable = true;
      })
      .addCase(LoadClcientsSolicitations.fulfilled, (state, { payload }) => {
        state.tableInfo = payload;
        state.isLoadingTable = false;
      })
      .addCase(LoadClcientsSolicitations.rejected, (state) => {
        state.isLoadingTable = false;
      })
      .addCase(GetRedemptionInfo.pending, (state) => {
        state.isLoadingOrderDetails = true;
      })
      .addCase(GetRedemptionInfo.fulfilled, (state, { payload }) => {
        state.orderDetailsInfo = payload;
        state.isLoadingOrderDetails = false;
      })
      .addCase(GetRedemptionInfo.rejected, (state) => {
        state.isLoadingOrderDetails = false;
      });
  },
});

export const {
  cleanTableInfo,
  changeCurrentPage,
  changePageSize,
  resetPageNumber,
} = ReportRedemptionSlice.actions;
export default ReportRedemptionSlice.reducer;
